import { React, classNames } from '@digsup/core-ui';
import PropTypes from 'prop-types';
import { Icon } from '@digsup/components-icon';
import { Image } from '@digsup/components-image';
import { Wrapper } from './Wrapper';
import { styles } from './Avatar.scss';

const cx = classNames.bind(styles);

/**
 * Avatar component
 ~~~js
 import { Avatar } from '@digsup/components-avatar';
 ~~~
 */

export const Avatar = ({
  src,
  type,
  size,
  variant,
  isInverted,
  isOutlined,
  isHoverable,
  className,
  children,
  ...props
}) => {
  let iconSize = '';
  let withBackground = true;
  if (size === 'small' && !children) {
    withBackground = false;
  }
  if (size === 'medium') {
    iconSize = 'medium';
  } else {
    iconSize = 'xlarge';
  }

  const classes = cx(
    'c-avatar',
    {
      [`c-avatar--type-${type}`]: type,
      [`c-avatar--${size}`]: size,
      [`c-avatar--with-bg`]: withBackground,
      [`c-avatar--variant-${variant}`]: variant,
      'c-avatar--inverted': isInverted,
      'c-avatar--outlined': isOutlined && src,
      'c-avatar--hoverable': isHoverable,
    },
    className,
  );

  return (
    <div {...props} className={classes}>
      {src ? (
        <Wrapper
          backgroundHorizontalAlign="center"
          backgroundVerticalAlign="center">
          <Image src={src} cover radius="full" alt="Avatar image" />
        </Wrapper>
      ) : (
        <Wrapper
          backgroundHorizontalAlign="center"
          backgroundVerticalAlign={
            size === 'small' || isInverted ? 'center' : 'bottom'
          }>
          <Icon name={isInverted ? 'user-small' : 'user'} size={iconSize} />
        </Wrapper>
      )}
      {children &&
        React.Children.map(children, child =>
          React.cloneElement(child, {
            className: child.props.slot
              ? `c-avatar__slot-${child.props.slot}`
              : '',
          }),
        )}
    </div>
  );
};

Avatar.displayName = 'Avatar';

Avatar.propTypes = {
  /**
   * Avatar image src
   */
  src: PropTypes.string,
  size: PropTypes.oneOf(['xxlarge', 'xlarge', 'large', 'medium', 'small']),
  children: PropTypes.any,
  type: PropTypes.oneOf([
    'co-owner',
    'sole-owner',
    'agent',
    'broker',
    'attorney',
    'family',
    'friend',
    'other',
    'renter',
    'dreamer',
  ]).isRequired,
  /**
   * Avater variant, user or property
   */
  variant: PropTypes.oneOf(['user', 'property']),
  /**
   * Inverts color and background color, uses different avatar icon
   */
  isInverted: PropTypes.bool,
  /**
   * Adds outline when image src is provided
   */
  isOutlined: PropTypes.bool,
  /**
   * Adds outline on hover when image src is provided
   */
  isHoverable: PropTypes.bool,
  /**
   * Extend the classNames applied to the component
   */
  className: PropTypes.string,
};

Avatar.defaultProps = {
  size: 'large',
  type: 'co-owner',
  variant: 'user',
  isInverted: false,
  isOutlined: false,
  isHoverable: false,
  children: '',
  className: '',
};
